/**
 * CreateDataAgentModal Component
 *
 * Modal for creating a new data agent.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useCreateDataAgent } from '../hooks/useCreateDataAgent';
import type { DataAgentCreateDTO } from '../../../types/data-agent.types';

interface CreateDataAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

export const CreateDataAgentModal: React.FC<CreateDataAgentModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<DataAgentCreateDTO>({
    defaultValues: {
      dataAgentCode: '',
      dataAgentName: '',
      description: '',
    },
  });

  const { mutate: createDataAgent, isPending } = useCreateDataAgent(tenantId, {
    onSuccess: () => {
      setSuccessMessage('Data agent created successfully!');
      setErrorMessage('');
      setTimeout(() => {
        reset();
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: DataAgentCreateDTO) => {
    setErrorMessage('');
    setSuccessMessage('');
    createDataAgent(data);
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      setAutoGenerateCode(true);
      onClose();
    }
  };

  // Auto-generate code from name
  const generateCodeFromName = (name: string): string => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);
  };

  const dataAgentName = watch('dataAgentName');

  useEffect(() => {
    if (autoGenerateCode && dataAgentName) {
      const generatedCode = generateCodeFromName(dataAgentName);
      setValue('dataAgentCode', generatedCode);
    }
  }, [dataAgentName, autoGenerateCode, setValue]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Create Data Agent</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          <FormField
            label="Agent Name"
            required
            error={errors.dataAgentName?.message}
            hint="A friendly name for this data agent"
          >
            <Input
              {...register('dataAgentName', {
                required: 'Agent name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' },
                maxLength: { value: 100, message: 'Name must be less than 100 characters' },
              })}
              placeholder="Production Agent"
              error={!!errors.dataAgentName}
            />
          </FormField>

          <FormField
            label="Agent Code"
            required
            error={errors.dataAgentCode?.message}
            hint="Code is immutable after creation"
          >
            <Input
              {...register('dataAgentCode', {
                required: 'Agent code is required',
                pattern: {
                  value: /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/,
                  message: 'Code must be lowercase alphanumeric with hyphens',
                },
                minLength: { value: 2, message: 'Code must be at least 2 characters' },
                maxLength: { value: 50, message: 'Code must be less than 50 characters' },
              })}
              placeholder="prod-agent"
              error={!!errors.dataAgentCode}
              disabled={autoGenerateCode}
            />
            <div className="mt-2 flex items-center">
              <Checkbox
                id="autoGenerateCode"
                checked={autoGenerateCode}
                onChange={(e) => setAutoGenerateCode(e.target.checked)}
              />
              <label htmlFor="autoGenerateCode" className="ml-2 text-sm text-gray-600">
                Auto-generate from name
              </label>
            </div>
          </FormField>

          <FormField
            label="Description"
            error={errors.description?.message}
            hint="Optional description of this agent's purpose"
          >
            <Textarea
              {...register('description', {
                maxLength: { value: 500, message: 'Description must be less than 500 characters' },
              })}
              placeholder="Data agent for production environment..."
              rows={3}
            />
          </FormField>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Create Agent
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
